import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { defineCustomElements } from '@rabobank/rds-components/dist/loader';
import { environment } from './environments/environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import type { ApplicationConfig} from '@angular/core';
import { importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { routes } from './app/app.routes';
import {
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
  MsalModule,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';

// Ensure the document language is explicitly set to English for built-in
// browser messages and any components that inspect `document.documentElement.lang`.
document.documentElement.lang = 'en';

const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideRouter(routes),
    importProvidersFrom(MsalModule),
    {
      provide: MSAL_INSTANCE,
      useFactory: () =>
        new PublicClientApplication({
          auth: {
            clientId: environment.azureAd.clientId,
            authority: environment.azureAd.authority,
            redirectUri: environment.azureAd.redirectUrl,
            clientCapabilities: ['CP1'],
          },
          cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
            storeAuthStateInCookie: false,
          },
          system: {
            loggerOptions: {
              loggerCallback: (_level: unknown, message: string, containsPii: boolean) => {
                if (containsPii) return;
                console.log('[MSAL]', message);
              },
              piiLoggingEnabled: false,
            },
          },
        }),
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: environment.scopes,
          // Request English UI from the identity provider (Azure AD / B2C)
          extraQueryParameters: { ui_locales: 'en-US' }
        },
      },
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
        useValue: {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map<string, string[]>([
          // Attach tokens for Microsoft Graph endpoints (used by GraphService)
          ['https://graph.microsoft.com/v1.0/', ['GroupMember.Read.All','User.Read']],
          [environment.resourceBatches(), environment.scopes],
        ]),
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));

defineCustomElements(window, {
    resourcesUrl: 'rds/',
});

// Mutation observer to translate known Dutch RDS validation messages to English.
// This is a lightweight fallback in case the RDS components render messages
// in Dutch despite passing `locale` attributes. It looks for specific Dutch
// phrases and replaces them with English equivalents.
(() => {
  const translations: { [k: string]: string } = {
    'De datum mag niet in het weekend liggen.': 'Date must not fall on a weekend.',
    'De datum mag niet op een feestdag vallen.': 'Date must not fall on a holiday.'
  };

  const replaceText = (node: Node) => {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      let text = node.nodeValue || '';
      for (const [nl, en] of Object.entries(translations)) {
        if (text.includes(nl)) {
          text = text.replaceAll(nl, en);
          node.nodeValue = text;
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Also check element's attributes and children
      const el = node as Element;
      if (el.childNodes && el.childNodes.length) {
        el.childNodes.forEach(replaceText);
      }
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'characterData' && m.target) {
        replaceText(m.target as Node);
      }
      if (m.addedNodes && m.addedNodes.length) {
        m.addedNodes.forEach(replaceText);
      }
    }
  });

  // Start observing the body once it's available
  const start = () => {
    if (document && document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
